import { inject, Injectable, LOCALE_ID, Provider } from '@angular/core';
import { ILanguage } from 'projects/shared/lib/interfaces/language/language.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { ILanguageService, ILanguageServiceToken } from './language-service.interface';

@Injectable({
  providedIn: 'root'
})
export class LanguageService implements ILanguageService {
  private readonly logger: LoggerService;
  private readonly LOCALEDELIMITER = '-';
  private fallbackLocale!: ILanguage;
  private currentLanguage: ILanguage;

  constructor() {
    this.logger = inject(LoggerService);
    let compileTimeLocaleCode = inject(LOCALE_ID);
    let localeData = this.localeDataGet(compileTimeLocaleCode);

    let infoLog = [];
    let errorLog = [];

    let allowedLanguageList: ILanguage[] = this.configuredLanguageListGet();

    let compileTimeLanguage: ILanguage | undefined = allowedLanguageList.find(
      (language: ILanguage) => {
        return localeData.languageCode.localeCompare(language.code) == 0;
      }
    );

    let envFallbackLanguage: ILanguage | undefined = ENVIRONMENT.locale.fallbackLanguage;

    if (envFallbackLanguage == undefined) {
      envFallbackLanguage = { code: 'de', name: 'Deutsch', flag: 'de' };
      infoLog.push(`No optional fallback language defined in environments. Using now "{ code: "${envFallbackLanguage.code}", name: "${envFallbackLanguage.name}", flag: "${envFallbackLanguage.flag}" }" as fallback language.`);

      for (let lanugage of allowedLanguageList) {
        infoLog.push(`Configured language by Environment: "{ code: "${lanugage.code}", name: "${lanugage.name}", flag: "${lanugage.flag}" }"`);
      }

    }

    this.fallbackLocale = envFallbackLanguage;

    if (compileTimeLanguage == undefined) {
      errorLog.push(`Compiled locale "${compileTimeLocaleCode}" is not configured in the environments. Using the fallback language.`);
      this.currentLanguage = envFallbackLanguage;
    } else {
      this.currentLanguage = compileTimeLanguage;
    }

    if (infoLog.length > 0) {
      this.logger.info(infoLog.join(`\n`), this.constructor.name);
    }
    if (errorLog.length > 0) {
      this.logger.error(errorLog.join(`\n`), this.constructor.name);
    }
  }

  public currentLocaleGet(): ILanguage | undefined {
    return this.currentLanguage;
  }

  public currentLocaleGetOrDefault(): ILanguage {
    let locale = this.currentLocaleGet();
    if (!locale) {
      locale = this.fallbackLocale;
    }
    return locale;
  }

  public configuredLanguageListGet(): ILanguage[] {
    return ENVIRONMENT.locale.languages;
  }

  private localeDataGet(locale: string): {
    locale: string,
    languageCode: string,
    regionCode?: string | undefined
  } {
    let isLocaleISOConform = locale.includes(this.LOCALEDELIMITER);
    if (isLocaleISOConform != true) {
      return { locale: locale, languageCode: locale };
    }

    let localeSplit = locale.split(this.LOCALEDELIMITER);
    let languageCode = localeSplit[0];
    let regionCode = localeSplit[1];
    return { locale: locale, languageCode: languageCode, regionCode: regionCode };
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const LanguageServiceProvider: Provider =
  { provide: ILanguageServiceToken, useExisting: LanguageService };
